import { Box, Button, Drawer, InputAdornment, TextField } from '@material-ui/core';
import { Lender } from '@nemuru-eng/js-api-client';
import { ChangeEvent, useEffect, useState } from 'react';

import { PricingsTable } from '@/components/shared/components/PricingsTable/PricingsTable';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { Font } from '@/components/shared/components/Font';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

import { SetPricingsDialog } from './components/SetPricingsDialog';
import { usePricingSelector } from './hooks/usePricingSelector';

interface PricingSelectorProps {
  agentId: string;
  lenders: Lender[];
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const PricingSelector = ({ agentId, lenders, open, setOpen }: PricingSelectorProps) => {
  const { palette } = useCustomTheme();
  const { getTextIdTranslation } = useTranslation();

  const {
    loading,
    setLoading,
    page,
    setPage,
    filteredPricings,
    selectedPricings,
    agentPricings,
    setFilteredAvailablePricings,
    getAgentPricings,
    searchAvailablePricing,
    selectPricing,
  } = usePricingSelector({ agentId });

  const { opened, setOpened } = useDialog();

  const [toggleSelectedPricings, setToggleSelectedPricings] = useState(false);

  useEffect(() => {
    setFilteredAvailablePricings();
  }, []);

  if (!filteredPricings) return null;

  const getAgentPricingsAndCloseDialog = async (): Promise<void> => {
    await getAgentPricings();
    setOpen(false);
  };

  const togglePricings = () => {
    setToggleSelectedPricings(!toggleSelectedPricings);
    setPage(0);
  };

  if (!open) return <></>;

  return (
    <>
      <Drawer anchor={'bottom'} open={open} onClose={() => setOpen(false)}>
        <Box px={8} py={4}>
          <Box display={'flex'} flexDirection={'column'} minHeight={660}>
            <Box mt={2} mb={6} display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
              <Font variant={'h4'} color={'title'}>
                <strong>
                  <Text tId={'ASSIGN_PRICINGS_TO_AGENT'} />
                </strong>
              </Font>
              <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'}>
                <TextField
                  placeholder={getTextIdTranslation('SEARCH_PRICING') as string}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => searchAvailablePricing(e.target.value)}
                  style={{ minWidth: '300px', marginTop: '-4px' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position={'start'}>
                        <i className={'material-icons-outlined'} style={{ fontSize: '22px', color: palette.grey[400] }}>
                          search
                        </i>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  size={'small'}
                  color={toggleSelectedPricings ? 'secondary' : 'default'}
                  variant={toggleSelectedPricings ? 'contained' : 'outlined'}
                  onClick={togglePricings}
                  startIcon={<>{toggleSelectedPricings && <i className={'material-icons-outlined'}>check</i>}</>}
                  style={{ minWidth: '180px', marginLeft: '32px' }}
                >
                  <Text tId={'SEE_SELECTED_PRICINGS'} />
                </Button>
              </Box>
            </Box>
            <Box flexGrow={1}>
              <PricingsTable
                pricings={toggleSelectedPricings ? selectedPricings : filteredPricings}
                lenders={lenders}
                selectPricing={selectPricing}
                page={page}
                setPage={setPage}
                defaultRowsPerPage={5}
                refreshData={async () => await getAgentPricings()}
              />
            </Box>
            <Box mt={3} mb={2} width={'100%'} display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
              <Font variant={'body2'} color={'textSecondary'}>
                <Text tId={'SELECTED_PRICINGS'} dynamicDataTexts={[selectedPricings.length]} />
              </Font>
              <Box display={'flex'} alignItems={'center'} gridGap={'16px'}>
                <StandardButton disabled={loading} onClick={() => setOpen(false)}>
                  <Text tId={'CLOSE'} />
                </StandardButton>
                <StandardButton main loading={loading} onClick={() => setOpened(true)}>
                  <Text tId={'SAVE'} />
                </StandardButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </Drawer>
      <SetPricingsDialog
        opened={opened}
        setLoading={setLoading}
        setOpened={setOpened}
        agentId={agentId}
        agentPricings={agentPricings}
        selectedPricings={selectedPricings}
        getAgentPricingsAndCloseDialog={getAgentPricingsAndCloseDialog}
      />
    </>
  );
};
